import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { usePeriod } from "@/context/PeriodContext";
import PeriodSelector, { ClosedPeriodBanner } from "@/components/PeriodSelector";
import TodayCards from "@/components/dashboard/TodayCards";
import QuickActions from "@/components/dashboard/QuickActions";
import MonthSummary from "@/components/dashboard/MonthSummary";
import RecentMovements from "@/components/dashboard/RecentMovements";
import UpcomingPayments from "@/components/dashboard/UpcomingPayments";
import DailyCloseCard from "@/components/dashboard/DailyCloseCard";
import { money, formatDateAT } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, TrendingUp, TrendingDown, Wallet, ArrowUpRight, ArrowDownRight,
  RefreshCcw, FileClock, AlertTriangle, Boxes, Timer, ArrowRight, CheckCircle2, RotateCcw,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import { SkeletonKPICard, SkeletonBox } from "@/components/Skeleton";
import EmptyState from "@/components/EmptyState";
import DataVerificationCard from "@/components/DataVerificationCard";
import LiveTodayCard from "@/components/LiveTodayCard";
import DayZeroCard from "@/components/DayZeroCard";
import ForecastWidget from "@/components/dashboard/ForecastWidget";
import StaffStatusWidget from "@/components/dashboard/StaffStatusWidget";
import { isStaffTier } from "@/lib/permissions";
import StaffDashboard from "@/pages/StaffDashboard";

// ---------------------------------------------------------------------------
//  Local helpers — no calculation, only presentation
// ---------------------------------------------------------------------------
const SEV = {
  ok:       { text: "text-emerald-700", bg: "bg-emerald-50",     border: "border-emerald-200", dot: "bg-emerald-500" },
  watch:    { text: "text-blue-700",    bg: "bg-blue-50",        border: "border-blue-200",    dot: "bg-blue-500" },
  warning:  { text: "text-amber-700",   bg: "bg-amber-50",       border: "border-amber-200",   dot: "bg-amber-500" },
  critical: { text: "text-destructive", bg: "bg-red-50",         border: "border-red-200",     dot: "bg-destructive" },
};

const REASON_LABEL = {
  en: {
    cash_runway_critical: "Cash reserves critically low",
    cash_runway_below_14d: "Less than 14 days of cash",
    cash_runway_below_30d: "Less than 30 days of cash",
    operating_at_loss: "Operating at a loss this month",
    low_profit_margin: "Profit margin is low",
    overdue_ap_exceeds_cash: "Overdue payables exceed cash",
    significant_overdue_ap: "Significant overdue payables",
    some_overdue_ap: "Some overdue payables",
    inventory_critical_stock: "Critical stock levels detected",
    inventory_low_stock: "Some items low in stock",
    expenses_up_significantly: "Expenses jumped significantly",
    expenses_trending_up: "Expenses trending upward",
    expense_growth_out_of_control: "Expense growth out of control",
    revenue_declining: "Revenue declining",
    revenue_dropping_fast: "Revenue dropping fast",
    insufficient_expense_history: "Not enough expense history",
    healthy: "Business is healthy",
  },
  tr: {
    cash_runway_critical: "Nakit rezervin çok düşük — kritik seviye",
    cash_runway_below_14d: "14 günden az nakit kaldı",
    cash_runway_below_30d: "30 günden az nakit kaldı",
    operating_at_loss: "Bu ay zararla çalışıyorsun",
    low_profit_margin: "Kâr marjı düşük",
    overdue_ap_exceeds_cash: "Vadesi geçmiş faturalar nakitten fazla",
    significant_overdue_ap: "Önemli miktarda vadesi geçmiş fatura var",
    some_overdue_ap: "Vadesi geçmiş faturalar var",
    inventory_critical_stock: "Kritik stok seviyesinde ürünler var",
    inventory_low_stock: "Bazı ürünler az stokta",
    expenses_up_significantly: "Giderler bu ay ciddi arttı",
    expenses_trending_up: "Giderler artış eğiliminde",
    expense_growth_out_of_control: "Gider büyümesi kontrolden çıktı",
    revenue_declining: "Ciro düşüş eğiliminde",
    revenue_dropping_fast: "Ciro hızlı düşüyor",
    insufficient_expense_history: "Yeterli gider geçmişi yok",
    healthy: "İşletme sağlıklı",
  },
  de: {
    cash_runway_critical: "Liquidität kritisch niedrig",
    cash_runway_below_14d: "Weniger als 14 Tage Cash",
    cash_runway_below_30d: "Weniger als 30 Tage Cash",
    operating_at_loss: "Verlust in diesem Monat",
    low_profit_margin: "Niedrige Gewinnmarge",
    overdue_ap_exceeds_cash: "Überfällige Rechnungen > Cash",
    significant_overdue_ap: "Bedeutende überfällige Rechnungen",
    some_overdue_ap: "Überfällige Rechnungen vorhanden",
    inventory_critical_stock: "Kritischer Lagerbestand",
    inventory_low_stock: "Einige Artikel niedrig",
    expenses_up_significantly: "Ausgaben stark gestiegen",
    expenses_trending_up: "Ausgaben steigen",
    expense_growth_out_of_control: "Ausgaben außer Kontrolle",
    revenue_declining: "Umsatz rückläufig",
    revenue_dropping_fast: "Umsatz fällt schnell",
    insufficient_expense_history: "Nicht genug Datenverlauf",
    healthy: "Alles gesund",
  },
};

function healthTone(risk) {
  switch (risk) {
    case "healthy": return "ok";
    case "watch":   return "watch";
    case "warning": return "warning";
    case "critical":return "critical";
    default:        return "watch";
  }
}

// Runway → severity
function runwaySev(days) {
  if (days === null || days === undefined) return "watch";
  if (days < 14) return "critical";
  if (days < 30) return "warning";
  if (days < 60) return "watch";
  return "ok";
}

// Revenue trend → severity (negative growth = bad)
function trendSev(pct) {
  if (pct === null || pct === undefined) return "watch";
  if (pct >= 5) return "ok";
  if (pct >= -5) return "watch";
  if (pct >= -15) return "warning";
  return "critical";
}

// ---------------------------------------------------------------------------
//  Dashboard
// ---------------------------------------------------------------------------
export default function Dashboard() {
  const { t, lang, tri } = useI18n();
  const { restaurant, user } = useAuth();
  const { periodKey, currentPeriodKey, isSelectedClosed } = usePeriod();
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [chart6m, setChart6m] = useState(null);
  const [loadError, setLoadError] = useState(null);            // Sprint 19 — user-visible network error

  // Hotfix — data-entry-only tier gets the simplified StaffDashboard,
  // no financial KPIs, cash/bank balances or forecasts are rendered.
  const isStaff = isStaffTier(user);

  const load = async (force = false) => {
    if (force) {
      try { await api.post("/bi/invalidate"); } catch { /* ignore */ }
    }
    try {
      const [o, d] = await Promise.all([
        api.get("/bi/overview"),
        api.get("/finance/dashboard"),
      ]);
      setOverview(o.data);
      setChart6m(d.data);
      setLoadError(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === "string" ? detail
        : detail?.message ? detail.message
        : tri({ en: "Could not load dashboard", de: "Dashboard konnte nicht geladen werden", tr: "Panel yüklenemedi" });
      setLoadError(msg);
    }
  };

  useEffect(() => {
    if (isStaff) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff]);

  if (isStaff) {
    return <StaffDashboard />;
  }

  const loading = !overview && !loadError;

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 animate-fade-rise">
        <div>
          <h1 className="font-display font-bold text-3xl md:text-[32px] tracking-[-0.02em] leading-tight" data-testid="dashboard-title">
            {tri({
              en: `Welcome, ${user?.full_name?.split(" ")[0] || ""}`,
              de: `Willkommen, ${user?.full_name?.split(" ")[0] || ""}`,
              tr: `Merhaba, ${user?.full_name?.split(" ")[0] || ""}`,
            })}
          </h1>
          <p className="text-sm text-muted-foreground mt-2 font-mono">
            {restaurant?.brand_name || restaurant?.name} · {formatDateAT(new Date().toISOString())}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodSelector />
          <Button variant="outline" size="sm" onClick={() => load(true)} data-testid="refresh-btn" className="h-9 rounded-lg">
            <RefreshCcw className="w-4 h-4 mr-2" /> {t("refresh")}
          </Button>
        </div>
      </div>

      <ClosedPeriodBanner />

      {loadError && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm flex items-center gap-2"
             data-testid="dashboard-error">
          <svg className="w-4 h-4 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v3m0 4h.01M4.93 19h14.14a2 2 0 001.732-3l-7.07-12a2 2 0 00-3.464 0l-7.07 12A2 2 0 004.93 19z" /></svg>
          <span className="text-destructive font-medium">{loadError}</span>
          <Button size="sm" variant="outline" className="ml-auto h-7 rounded-md text-xs"
                  onClick={() => load(true)} data-testid="dashboard-retry">
            {t("retry")}
          </Button>
        </div>
      )}

      {/* Sprint 22 — Financial Workspace: Today · Quick Actions · Month */}
      <TodayCards />
      <QuickActions />
      <MonthSummary />

      {/* Sprint 16 — Day Zero live-start banner / wizard entry */}
      <DayZeroCard onModeChanged={() => load(true)} />

      {/* Attention Row */}
      {loading
        ? <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[0,1,2,3].map((i) => <SkeletonBox key={i} className="h-20" />)}</div>
        : <AttentionRow overview={overview} nav={nav} lang={lang} tri={tri} />}

      {/* Health + Priorities row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {loading || !overview?.health
          ? <><SkeletonBox className="h-56" /><SkeletonBox className="h-56 lg:col-span-2" /></>
          : <>
            <BusinessHealthCard health={overview.health} lang={lang} tri={tri} />
            <TodaysPriorities overview={overview} lang={lang} tri={tri} nav={nav} />
          </>}
      </div>

      {/* Sprint 22 — Recent Movements + Upcoming Payments (side by side on lg) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RecentMovements />
        <UpcomingPayments />
      </div>

      {/* Sprint 26 — Cash Flow Forecast (Next 30 days) */}
      <ForecastWidget />

      {/* Sprint 22 — Daily Close */}
      <DailyCloseCard />

      {/* Sprint 29.1 — Staff Status widget for owners/managers */}
      <StaffStatusWidget />

      {/* Sprint 15 — Live Operating layer (Today · Cash · Assistant) */}
      <LiveTodayCard />

      {/* Sprint 14 — Data verification layer */}
      <DataVerificationCard />

      {/* Chart + Upcoming (6-month trend) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard chart6m={chart6m} t={t} lang={lang} />
        <UpcomingCard cashflow={overview?.cashflow} lang={lang} tri={tri} t={t} />
      </div>

      {/* Persistent AI insight */}
      <PersistentAiInsight lang={lang} tri={tri} />
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Attention Row  (4 severity chips → link to filter)
// ---------------------------------------------------------------------------
function AttentionRow({ overview, nav, lang, tri }) {
  const inv = overview?.invoices;
  const runway = overview?.runway;
  const inventory = overview?.inventory;
  const revenue = overview?.revenue;
  if (!inv || !runway || !inventory || !revenue) return null;

  const items = [
    {
      key: "overdue",
      sev: inv.ap.overdue_count === 0 ? "ok" : (inv.ap.overdue_total > (runway.current_cash || 1) ? "critical" : "warning"),
      icon: FileClock,
      label: tri({ en: "Overdue", de: "Überfällig", tr: "Vadesi Geçmiş" }),
      value: inv.ap.overdue_count,
      hint: inv.ap.overdue_count === 0
        ? tri({ en: "Clean", de: "Keine", tr: "Temiz" })
        : money(inv.ap.overdue_total),
      onClick: () => nav("/finance?tab=invoices&filter=overdue"),
      testid: "attn-overdue",
    },
    {
      key: "stock",
      sev: inventory.critical_count > 0 ? "critical" : (inventory.low_count > 0 ? "warning" : "ok"),
      icon: Boxes,
      label: tri({ en: "Critical Stock", de: "Kritischer Bestand", tr: "Kritik Stok" }),
      value: inventory.critical_count + inventory.low_count,
      hint: inventory.critical_count > 0
        ? `${inventory.critical_count} ${tri({ en: "critical", de: "kritisch", tr: "kritik" })}`
        : (inventory.low_count > 0
            ? `${inventory.low_count} ${tri({ en: "low", de: "niedrig", tr: "az" })}`
            : tri({ en: "Healthy", de: "OK", tr: "Sağlıklı" })),
      onClick: () => nav(inventory.critical_count > 0 ? "/inventory?stock=critical" : (inventory.low_count > 0 ? "/inventory?stock=low" : "/inventory")),
      testid: "attn-stock",
    },
    {
      key: "runway",
      sev: runwaySev(runway.days_of_cash_remaining),
      icon: Timer,
      label: tri({ en: "Cash Runway", de: "Cash-Reichweite", tr: "Nakit Ömrü" }),
      value: runway.days_of_cash_remaining === null ? "—" : `${runway.days_of_cash_remaining}${tri({ en: "d", de: "T", tr: "g" })}`,
      hint: `${money(runway.avg_daily_expenses)}/${tri({ en: "d", de: "T", tr: "gün" })}`,
      onClick: () => nav("/finance?tab=cashflow"),
      testid: "attn-runway",
    },
    {
      key: "revenue",
      sev: trendSev(revenue.growth_pct),
      icon: revenue.growth_pct >= 0 ? TrendingUp : TrendingDown,
      label: tri({ en: "Revenue Trend", de: "Umsatz-Trend", tr: "Ciro Trendi" }),
      value: revenue.growth_pct === null ? "—" : `${revenue.growth_pct >= 0 ? "+" : ""}${revenue.growth_pct.toFixed(1)}%`,
      hint: money(revenue.monthly),
      onClick: () => nav("/finance?tab=revenue&period=this_month"),
      testid: "attn-revenue",
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-fade-rise">
      {items.map((i) => {
        const s = SEV[i.sev];
        const Icon = i.icon;
        return (
          <button
            key={i.key}
            onClick={i.onClick}
            data-testid={i.testid}
            className={`text-left rounded-xl border ${s.border} ${s.bg} p-4 transition-all hover:shadow-elev-3 hover:-translate-y-0.5 group`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${s.dot} ${i.sev === "critical" ? "animate-pulse" : ""}`} />
                <span className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${s.text}`}>{i.label}</span>
              </div>
              <Icon className={`w-4 h-4 ${s.text} opacity-70`} />
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <div className={`font-mono text-2xl font-bold tracking-[-0.02em] ${s.text}`}>{i.value}</div>
              <div className="text-xs text-muted-foreground truncate font-mono">{i.hint}</div>
            </div>
            <div className="mt-2 flex items-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground group-hover:text-foreground transition-colors">
              {tri({ en: "Details", de: "Details", tr: "Detay" })} <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Business Health Card
// ---------------------------------------------------------------------------
function BusinessHealthCard({ health, lang, tri }) {
  const tone = healthTone(health.risk_level);
  const s = SEV[tone];
  const label = {
    healthy:  tri({ en: "Healthy",  de: "Gesund",     tr: "Sağlıklı" }),
    watch:    tri({ en: "Watch",    de: "Beobachten", tr: "İzle" }),
    warning:  tri({ en: "Warning",  de: "Warnung",    tr: "Uyarı" }),
    critical: tri({ en: "Critical", de: "Kritisch",   tr: "Kritik" }),
  }[health.risk_level] || health.risk_level;
  const reason = REASON_LABEL[lang]?.[health.main_reason] || REASON_LABEL.en?.[health.main_reason] || health.main_reason;

  // Arc SVG (0..100)
  const pct = Math.max(0, Math.min(100, health.score));
  const R = 46; const C = 2 * Math.PI * R;
  const dash = (pct / 100) * C;

  return (
    <Card className={`border rounded-xl shadow-elev-1 relative overflow-hidden animate-fade-rise-1 ${s.border}`} data-testid="health-card">
      <div className={`absolute inset-0 pointer-events-none opacity-40 ${s.bg}`} />
      <CardHeader className="pb-2 relative">
        <CardTitle className="text-base font-display font-semibold tracking-tight flex items-center gap-2">
          <CheckCircle2 className={`w-4 h-4 ${s.text}`} />
          {tri({ en: "Business Health", de: "Geschäftsgesundheit", tr: "İşletme Sağlığı" })}
        </CardTitle>
      </CardHeader>
      <CardContent className="relative flex items-center gap-5">
        <div className="relative shrink-0" style={{ width: 116, height: 116 }}>
          <svg width="116" height="116" viewBox="0 0 116 116" className="-rotate-90">
            <circle cx="58" cy="58" r={R} fill="none" stroke="hsl(var(--muted))" strokeWidth="10" />
            <circle
              cx="58" cy="58" r={R} fill="none" strokeLinecap="round" strokeWidth="10"
              stroke={
                tone === "ok" ? "hsl(158 64% 42%)" :
                tone === "watch" ? "hsl(217 91% 60%)" :
                tone === "warning" ? "hsl(40 96% 55%)" : "hsl(0 84% 60%)"
              }
              strokeDasharray={`${dash} ${C - dash}`}
              style={{ transition: "stroke-dasharray 600ms cubic-bezier(0.16, 1, 0.3, 1)" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className={`font-mono text-3xl font-bold tracking-[-0.02em] ${s.text}`} data-testid="health-score">{health.score}</div>
            <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">/100</div>
          </div>
        </div>
        <div className="min-w-0">
          <Badge variant="outline" className={`${s.text} ${s.bg} ${s.border} rounded-md font-semibold text-[10px] uppercase tracking-wider`} data-testid="health-level">
            {label}
          </Badge>
          <p className="text-sm font-semibold mt-2 leading-tight" data-testid="health-reason">{reason}</p>
          <div className="mt-3 grid grid-cols-3 gap-2 text-[10px]">
            {Object.entries(health.components).slice(0, 6).map(([k, v]) => (
              <div key={k} className="flex flex-col">
                <span className="uppercase tracking-wider text-muted-foreground truncate font-semibold">{k.replace("_", " ")}</span>
                <span className="font-mono font-semibold">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
//  Today's Priorities  — top 3 derived from BI signals (no new calc)
// ---------------------------------------------------------------------------
function derivePriorities(o, lang, tri) {
  const out = [];
  if (!o?.health?.signals || !o?.runway || !o?.invoices) return out;
  const s = o.health.signals;
  const cash = o.runway.current_cash;

  // 1. Overdue AP with cash available → PAY IT
  const overdueAp = o.invoices.ap;
  if (overdueAp.overdue_count > 0) {
    if (cash >= overdueAp.overdue_total) {
      out.push({
        sev: "critical",
        icon: FileClock,
        title: tri({ en: "Pay overdue invoices", de: "Überfällige Rechnungen bezahlen", tr: "Vadesi geçmiş faturaları öde" }),
        detail: tri({
          en: `${overdueAp.overdue_count} invoices · ${money(overdueAp.overdue_total)} · cash sufficient`,
          de: `${overdueAp.overdue_count} Rechnungen · ${money(overdueAp.overdue_total)} · Cash reicht`,
          tr: `${overdueAp.overdue_count} fatura · ${money(overdueAp.overdue_total)} · nakit yeterli`,
        }),
        href: "/finance?tab=invoices&filter=overdue",
        primary: { label: tri({ en: "Open invoices", de: "Rechnungen öffnen", tr: "Faturaları aç" }), href: "/finance?tab=invoices&filter=overdue" },
      });
    } else {
      out.push({
        sev: "critical",
        icon: FileClock,
        title: tri({ en: "Negotiate longer payment terms", de: "Zahlungsziel verhandeln", tr: "Vade uzatma pazarlığı yap" }),
        detail: tri({
          en: `Overdue ${money(overdueAp.overdue_total)} > cash ${money(cash)}`,
          de: `Überfällig ${money(overdueAp.overdue_total)} > Cash ${money(cash)}`,
          tr: `Vadesi geçen ${money(overdueAp.overdue_total)} > nakit ${money(cash)}`,
        }),
        href: "/finance?tab=invoices&filter=overdue",
        primary: { label: tri({ en: "View suppliers", de: "Lieferanten anzeigen", tr: "Tedarikçileri gör" }), href: "/finance?tab=suppliers" },
        secondary: { label: tri({ en: "Invoices", de: "Rechnungen", tr: "Faturalar" }), href: "/finance?tab=invoices&filter=overdue" },
      });
    }
  }

  // 2. Critical stock → ORDER
  if (o.inventory.critical_count > 0) {
    const item = (o.inventory.top_items || []).find((it) => it.health === "critical") || null;
    out.push({
      sev: "critical",
      icon: Boxes,
      title: item
        ? tri({ en: `Order ${item.name}`, de: `${item.name} bestellen`, tr: `${item.name} siparişi ver` })
        : tri({ en: "Order critical items", de: "Kritische Artikel bestellen", tr: "Kritik ürünlere sipariş ver" }),
      detail: tri({
        en: `${o.inventory.critical_count} critical${o.inventory.low_count ? ` · ${o.inventory.low_count} low` : ""}`,
        de: `${o.inventory.critical_count} kritisch${o.inventory.low_count ? ` · ${o.inventory.low_count} niedrig` : ""}`,
        tr: `${o.inventory.critical_count} kritik${o.inventory.low_count ? ` · ${o.inventory.low_count} az` : ""}`,
      }),
      href: "/inventory?stock=critical",
      primary: { label: tri({ en: "Show critical", de: "Kritische zeigen", tr: "Kritikleri gör" }), href: "/inventory?stock=critical" },
    });
  } else if (o.inventory.low_count > 0) {
    out.push({
      sev: "warning",
      icon: Boxes,
      title: tri({ en: "Plan low-stock items", de: "Niedrigen Bestand einplanen", tr: "Az kalan stokları planla" }),
      detail: `${o.inventory.low_count} ${tri({ en: "items", de: "Artikel", tr: "kalem" })}`,
      href: "/inventory?stock=low",
      primary: { label: tri({ en: "Show low", de: "Niedrige zeigen", tr: "Azları gör" }), href: "/inventory?stock=low" },
    });
  }

  // 3. Runway short → REDUCE / RAISE
  if (s.days_of_cash_remaining !== null && s.days_of_cash_remaining < 30) {
    out.push({
      sev: s.days_of_cash_remaining < 14 ? "critical" : "warning",
      icon: Timer,
      title: tri({ en: "Extend cash runway", de: "Cash-Reichweite verlängern", tr: "Nakit ömrünü uzat" }),
      detail: tri({
        en: `${s.days_of_cash_remaining} days · Ø ${money(o.runway.avg_daily_expenses)}/d`,
        de: `${s.days_of_cash_remaining} Tage · Ø ${money(o.runway.avg_daily_expenses)}/T`,
        tr: `${s.days_of_cash_remaining} gün kaldı · ort. ${money(o.runway.avg_daily_expenses)}/gün`,
      }),
      href: "/finance?tab=cashflow",
      primary: { label: tri({ en: "Cash flow", de: "Cashflow", tr: "Nakit akışı" }), href: "/finance?tab=cashflow" },
      secondary: { label: tri({ en: "Expenses", de: "Ausgaben", tr: "Giderler" }), href: "/finance?tab=expenses&period=this_month" },
    });
  }

  // 4. Expenses growing → REDUCE
  if (s.expense_growth_pct !== null && s.expense_growth_pct > 15) {
    const cat = o.expenses.biggest_categories?.[0];
    const catId = cat?.category_id;
    const catName = cat ? (cat.name?.[lang] || cat.name?.en || cat.name?.de || cat.name?.tr) : "";
    out.push({
      sev: s.expense_growth_pct > 30 ? "critical" : "warning",
      icon: TrendingUp,
      title: cat
        ? tri({ en: `Review ${catName} expenses`, de: `${catName} prüfen`, tr: `${catName} giderlerini gözden geçir` })
        : tri({ en: "Review expenses", de: "Ausgaben prüfen", tr: "Giderleri gözden geçir" }),
      detail: `${tri({ en: "Growth", de: "Wachstum", tr: "Aylık artış" })} +${s.expense_growth_pct.toFixed(1)}%`,
      href: catId ? `/finance?tab=expenses&category_id=${catId}&period=this_month` : "/finance?tab=expenses&period=this_month",
      primary: { label: tri({ en: "Open expenses", de: "Ausgaben öffnen", tr: "Giderleri aç" }), href: catId ? `/finance?tab=expenses&category_id=${catId}&period=this_month` : "/finance?tab=expenses&period=this_month" },
    });
  }

  // 5. Revenue dropping → ACT
  if (s.revenue_growth_pct !== null && s.revenue_growth_pct < -5) {
    out.push({
      sev: s.revenue_growth_pct < -15 ? "critical" : "warning",
      icon: TrendingDown,
      title: tri({ en: "Find the channel driving the revenue drop", de: "Umsatzrückgang analysieren", tr: "Ciro düşüşünü tetikleyen kanalı bul" }),
      detail: `${s.revenue_growth_pct.toFixed(1)}% ${tri({ en: "vs. last month", de: "vs. Vormonat", tr: "geçen aya göre" })}`,
      href: "/finance?tab=revenue&period=this_month",
      primary: { label: tri({ en: "Open revenue", de: "Umsatz öffnen", tr: "Ciroyu aç" }), href: "/finance?tab=revenue&period=this_month" },
      secondary: { label: tri({ en: "Reports", de: "Berichte", tr: "Raporlar" }), href: "/reports" },
    });
  }

  // 6. Loss operation → REVIEW
  if (s.net_margin_pct !== null && s.net_margin_pct < 0) {
    out.push({
      sev: "critical",
      icon: AlertTriangle,
      title: tri({ en: "You're at a loss — review prices and costs", de: "Verlust — Preise & Kosten prüfen", tr: "Zarardasın — fiyat ve maliyet gözden geçir" }),
      detail: `${tri({ en: "Margin", de: "Marge", tr: "Kâr marjı" })} ${s.net_margin_pct.toFixed(1)}%`,
      href: "/reports",
      primary: { label: tri({ en: "Reports", de: "Berichte", tr: "Raporlar" }), href: "/reports" },
    });
  }

  // Prioritise critical, then warning; return top 3
  const order = { critical: 0, warning: 1, watch: 2, ok: 3 };
  return out.sort((a, b) => (order[a.sev] ?? 9) - (order[b.sev] ?? 9)).slice(0, 3);
}

function TodaysPriorities({ overview, lang, tri, nav }) {
  const priorities = useMemo(() => derivePriorities(overview, lang, tri), [overview, lang, tri]);

  return (
    <Card className="border rounded-xl shadow-elev-1 lg:col-span-2 animate-fade-rise-2" data-testid="priorities-card">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base font-display font-semibold tracking-tight">
            {tri({ en: "Today's priorities", de: "Heutige Prioritäten", tr: "Bugünün Öncelikleri" })}
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            {tri({ en: "Derived from BI · top 3 highest-impact tasks", de: "Aus BI abgeleitet · Top 3", tr: "İş zekasından türetildi · en yüksek etkili 3 iş" })}
          </p>
        </div>
        <Badge variant="secondary" className="rounded-md text-[10px]">{priorities.length}/3</Badge>
      </CardHeader>
      <CardContent className="p-0">
        {priorities.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={CheckCircle2}
              title={tri({ en: "Nothing urgent today", de: "Nichts Dringendes heute", tr: "Bugün acil bir iş yok" })}
              description={tri({ en: "All indicators look healthy. Keep it up!", de: "Alle Indikatoren gesund.", tr: "Tüm göstergeler sağlıklı. Devam et!" })}
            />
          </div>
        ) : (
          <div className="divide-y">
            {priorities.map((p, i) => {
              const s = SEV[p.sev];
              const Icon = p.icon;
              return (
                <div key={i} className="flex items-start gap-3 px-4 py-3.5 hover:bg-muted/40 transition-colors" data-testid={`priority-${i}`}>
                  <button
                    onClick={() => nav(p.href)}
                    className={`w-9 h-9 rounded-lg ${s.bg} border ${s.border} flex items-center justify-center shrink-0 hover:scale-105 transition-transform`}
                    aria-label={p.title}
                  >
                    <Icon className={`w-4 h-4 ${s.text}`} strokeWidth={2.2} />
                  </button>
                  <button
                    onClick={() => nav(p.href)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-sm truncate">{p.title}</div>
                      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 font-mono truncate">{p.detail}</div>
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    {p.secondary && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); nav(p.secondary.href); }}
                        className="h-7 rounded-md text-xs hidden sm:inline-flex"
                        data-testid={`priority-${i}-secondary`}
                      >
                        {p.secondary.label}
                      </Button>
                    )}
                    {p.primary && (
                      <Button
                        size="sm"
                        variant={p.sev === "critical" ? "default" : "outline"}
                        onClick={(e) => { e.stopPropagation(); nav(p.primary.href); }}
                        className="h-7 rounded-md text-xs"
                        data-testid={`priority-${i}-action`}
                      >
                        {p.primary.label}
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
//  KPI Row — 4 core metrics
// ---------------------------------------------------------------------------
function KpiRow({ overview, t, lang, tri }) {
  const hintFor = (basis) =>
    basis === "trailing_30d" ? tri({ en: "Last 30 days", de: "Letzte 30 Tage", tr: "Son 30 gün" }) : null;
  const accountsWord = (n) => n === 1 ? tri({ en: "account", de: "Konto", tr: "hesap" }) : tri({ en: "accounts", de: "Konten", tr: "hesap" });
  const items = [
    { label: t("revenueMtd"), value: money(overview.revenue.monthly), delta: overview.revenue.growth_pct, icon: TrendingUp, testid: "kpi-revenue", basisHint: hintFor(overview.revenue.basis) },
    { label: t("expensesMtd"), value: money(overview.expenses.monthly), delta: overview.expenses.growth_pct, invert: true, icon: TrendingDown, testid: "kpi-expenses", basisHint: hintFor(overview.expenses.basis) },
    { label: t("profitMtd"), value: money(overview.profit.net_profit), delta: overview.profit.net_profit_growth_pct, icon: Wallet, testid: "kpi-profit", accent: true, basisHint: hintFor(overview.profit.basis) },
    { label: t("cashOnHand"), value: money(overview.cashflow.current_cash), sub: `${overview.cashflow.accounts_count} ${accountsWord(overview.cashflow.accounts_count)}`, icon: Wallet, testid: "kpi-cash" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
      {items.map((k, i) => <Kpi key={i} {...k} />)}
    </div>
  );
}

function Kpi({ label, value, delta, sub, accent, invert, icon: Icon, testid, basisHint }) {
  const isPos = delta !== null && delta !== undefined && (invert ? delta <= 0 : delta >= 0);
  return (
    <Card className="border rounded-xl shadow-elev-1 card-hover animate-fade-rise-1">
      <CardContent className="p-5">
        <div className="flex items-center justify-between text-muted-foreground mb-2.5">
          <span className="text-[10px] uppercase tracking-[0.14em] font-semibold truncate">{label}</span>
          {Icon && <div className="w-7 h-7 rounded-lg bg-muted/60 flex items-center justify-center shrink-0"><Icon className="w-3.5 h-3.5" /></div>}
        </div>
        <div className={`font-mono text-2xl md:text-[26px] font-semibold tracking-[-0.02em] ${accent ? "text-primary" : ""}`} data-testid={testid}>{value}</div>
        <div className="mt-1.5 flex items-center gap-2 text-xs h-4">
          {delta !== null && delta !== undefined && (
            <span className={`font-mono font-semibold inline-flex items-center gap-0.5 ${isPos ? "text-emerald-600" : "text-destructive"}`}>
              {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%
            </span>
          )}
          {basisHint && <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`${testid}-basis`}>{basisHint}</span>}
          {sub && <span className="text-muted-foreground truncate">{sub}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
//  6M chart + Upcoming — unchanged data source
// ---------------------------------------------------------------------------
function ChartCard({ chart6m, t, lang }) {
  return (
    <Card className="lg:col-span-2 border rounded-xl shadow-elev-1 animate-fade-rise-2">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base font-display font-semibold tracking-tight">{t("last6Months")}</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">{t("revenueVsExpenses")}</p>
        </div>
        <Badge variant="secondary" className="font-mono text-[10px] rounded-md">6M</Badge>
      </CardHeader>
      <CardContent className="h-64 pt-2">
        {chart6m ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart6m.chart_6m} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="4 4" vertical={false} />
              <XAxis dataKey="month" fontSize={11} stroke="hsl(var(--muted-foreground))" axisLine={false} tickLine={false} />
              <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `€${v}`} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => money(v)} cursor={{ fill: "hsl(var(--muted) / 0.3)" }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" />
              <Bar dataKey="revenue" name={t("revenue")} fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} maxBarSize={40} />
              <Bar dataKey="expenses" name={t("expenses")} fill="hsl(0 84% 60%)" radius={[6, 6, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        ) : <SkeletonBox className="h-full w-full" />}
      </CardContent>
    </Card>
  );
}

function UpcomingCard({ cashflow, lang }) {
  const bucket = cashflow?.upcoming_payments?.d14 || { count: 0, total: 0 };
  return (
    <Card className="border rounded-xl shadow-elev-1 animate-fade-rise-3">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base font-display font-semibold tracking-tight">{lang === "tr" ? "Yaklaşan Ödemeler" : "Anstehende Zahlungen"}</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">14 {lang === "tr" ? "gün" : "Tage"}</p>
        </div>
        <Link to="/finance?tab=invoices" className="text-xs text-primary hover:underline inline-flex items-center gap-0.5">
          {lang === "tr" ? "Tümü" : "Alle"} <ArrowRight className="w-3 h-3" />
        </Link>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline justify-between">
          <div>
            <div className="font-mono text-3xl font-bold tracking-[-0.02em]" data-testid="upcoming-total">{money(bucket.total)}</div>
            <div className="text-xs text-muted-foreground mt-1 font-mono">{bucket.count} {lang === "tr" ? "fatura" : "Rechnungen"}</div>
          </div>
          <Badge variant={bucket.count === 0 ? "secondary" : "outline"} className={`rounded-md ${bucket.count === 0 ? "" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
            <ArrowDownRight className="w-3 h-3 mr-1" /> 14g
          </Badge>
        </div>
        <div className="border-t mt-4 pt-4 space-y-2">
          <Row7 label="7g" bucket={cashflow?.upcoming_payments?.d7} />
          <Row7 label="30g" bucket={cashflow?.upcoming_payments?.d30} />
        </div>
      </CardContent>
    </Card>
  );
}

function Row7({ label, bucket }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono font-semibold">{money(bucket?.total || 0)}</span>
      <span className="text-muted-foreground font-mono">{bucket?.count || 0}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Persistent AI insight — 24h localStorage cache + Regenerate
// ---------------------------------------------------------------------------
const AI_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 h

function loadCachedInsight(rid) {
  try {
    const raw = localStorage.getItem(`gp_ai_${rid}`);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.text) return null;
    if (Date.now() - obj.ts > AI_CACHE_TTL_MS) return null;
    return obj;
  } catch { return null; }
}
function saveCachedInsight(rid, text) {
  try { localStorage.setItem(`gp_ai_${rid}`, JSON.stringify({ text, ts: Date.now() })); } catch { /* noop */ }
}

function PersistentAiInsight({ lang, tri }) {
  const { restaurant } = useAuth();
  const rid = restaurant?.id || "anon";
  const [cached, setCached] = useState(() => loadCachedInsight(rid));
  const [loading, setLoading] = useState(false);

  useEffect(() => { setCached(loadCachedInsight(rid)); }, [rid]);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai/daily-insight");
      if (data.insight) {
        saveCachedInsight(rid, data.insight);
        setCached({ text: data.insight, ts: Date.now() });
      } else {
        toast.error("AI");
      }
    } catch { toast.error("AI"); } finally { setLoading(false); }
  };

  const age = cached ? Math.round((Date.now() - cached.ts) / 3600000) : null;

  return (
    <Card className="border rounded-xl shadow-elev-1 relative overflow-hidden animate-fade-rise-3">
      <div className="absolute -top-16 -right-16 w-48 h-48 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 relative">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div>
            <CardTitle className="text-base font-display font-semibold tracking-tight">
              {tri({ en: "AI Financial Summary", de: "KI-Finanzzusammenfassung", tr: "AI Finansal Özet" })}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              {cached
                ? tri({ en: `Generated ${age} h ago`, de: `vor ${age} h erstellt`, tr: `${age} saat önce oluşturuldu` })
                : tri({ en: "Not yet generated today", de: "Heute noch nicht erstellt", tr: "Bugün için henüz üretilmedi" })}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant={cached ? "outline" : "default"}
          onClick={generate}
          disabled={loading}
          data-testid="ai-regenerate-btn"
          className="h-9 rounded-lg"
        >
          {loading
            ? <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
            : cached
              ? <RotateCcw className="w-4 h-4 mr-2" />
              : <Sparkles className="w-4 h-4 mr-2" />}
          {loading
            ? tri({ en: "Generating…", de: "Erstellt…", tr: "Üretiliyor…" })
            : cached
              ? tri({ en: "Regenerate", de: "Neu erzeugen", tr: "Yeniden üret" })
              : tri({ en: "Generate summary", de: "Zusammenfassung", tr: "Özet oluştur" })}
        </Button>
      </CardHeader>
      <CardContent className="relative">
        {cached ? (
          <p className="text-sm leading-relaxed" data-testid="ai-insight-text">{cached.text}</p>
        ) : (
          <p className="text-sm text-muted-foreground leading-relaxed">
            {tri({
              en: "Click the button — the AI will produce a short financial summary of your operation. The result is cached for 24 hours.",
              de: "Klicke oben — die KI erstellt eine 24 h gültige Zusammenfassung.",
              tr: "Butona tıkla — AI, işletmen için kısa bir finansal özet üretsin. Sonuç 24 saat boyunca saklanır.",
            })}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
